import React, { useState, useRef } from "react";
import { Link, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { CircularProgress } from "@material-ui/core";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import WBlogoBlue from "../../../assets/icons/blueWBlogo.svg";
import { uploadFile } from "../../../utils/helpers";
import { postExternalMatchScorePreview } from "../../../services/matchScoreService";
import {
  persistMatchPreviewHandoff,
  setPageToVisitJobMatchScore,
} from "../../../constants/matchPreviewHandoff";
import "./index.scss";

const JobMatchScorePreview = () => {
  const { isAuthenticated } = useSelector((store) => store.auth);
  const [activeTab, setActiveTab] = useState("description");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [uploadingResume, setUploadingResume] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const resumeInputRef = useRef(null);

  if (isAuthenticated) {
    return <Redirect to="/job-match-score" />;
  }

  const triggerResumePicker = () => {
    if (!uploadingResume && resumeInputRef.current) {
      resumeInputRef.current.click();
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a PDF or DOC/DOCX resume.");
      return;
    }

    setUploadingResume(true);
    setError(null);
    try {
      const uploadResult = await uploadFile(
        file,
        "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload",
      );
      if (!uploadResult?.success || !uploadResult?.data) {
        throw new Error("Upload failed");
      }
      setResumeUrl(uploadResult.data);
      setResumeName(file.name || uploadResult.fileName || "Resume");
    } catch (uploadErr) {
      console.error(uploadErr);
      setError("Unable to upload resume. Please try again.");
    } finally {
      setUploadingResume(false);
    }
  };

  const persistHandoffAndAuthPath = () => {
    persistMatchPreviewHandoff({
      resumeUrl,
      resumeName,
      jobUrl,
      jobDescription,
      activeTab,
      ...(preview && typeof preview === "object"
        ? { rawResponse: preview }
        : {}),
    });
    setPageToVisitJobMatchScore();
  };

  const handleCheckMatchScore = async () => {
    setError(null);
    if (!resumeUrl) {
      setError("Please upload your resume first.");
      return;
    }
    if (activeTab === "url" && !String(jobUrl).trim()) {
      setError("Please enter a job posting URL.");
      return;
    }
    if (activeTab === "description" && !String(jobDescription).trim()) {
      setError("Please paste a job description.");
      return;
    }

    setChecking(true);
    setPreview(null);
    try {
      const result = await postExternalMatchScorePreview({
        resume_url: resumeUrl,
        job_url: activeTab === "url" ? String(jobUrl).trim() : "",
        job_description:
          activeTab === "description"
            ? String(jobDescription).trim()
            : "",
      });
      if (result.testUnavailable) {
        return;
      }
      if (result.success && result.data) {
        setPreview(result.data);
      } else {
        setError(result.error || "Could not compute your match score.");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.detail?.[0]?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setChecking(false);
    }
  };

  const score = Math.min(
    100,
    Math.max(0, Number(preview?.match_score ?? preview?.matchScore ?? 0)),
  );
  const jobInfo = preview?.job_info || {};

  return (
    <div className="jmsp-root">
      <Helmet>
        <title>Check Your Job Match Score | WorkBrook</title>
        <meta
          name="description"
          content="Upload your resume and see how well you match a job — powered by WorkBrook AI."
        />
      </Helmet>

      <header className="jmsp-header">
        <Link to="/signin">
          <img src={WBlogoBlue} alt="WorkBrook" className="jmsp-logo" />
        </Link>
        <nav className="jmsp-header-links">
          <Link to="/signin">Sign in</Link>
          <Link to="/signup">Create account</Link>
        </nav>
      </header>

      <main className="jmsp-main">
        {!preview && (
          <>
            <h1 className="jmsp-hero-title">Check Your Job Match Score</h1>
            <p className="jmsp-hero-copy">
              See how closely your resume aligns with any role before you sign
              in. Our AI compares your experience to the job posting and
              returns an instant match score — no account required for this
              preview.
            </p>
            <div className="jmsp-trust">
              <span>Private resume upload</span>
              <span>Preview only — sign in for full analysis</span>
              <span>Built for job seekers on workbrook</span>
            </div>

            <div className="jmsp-card">
              <div className="jmsp-step-label">Step 1 — Resume</div>
              <div className="jmsp-resume-row">
                <div className="jmsp-resume-meta">
                  {resumeUrl ? (
                    <>
                      <strong>{resumeName || "Resume"}</strong> ready to
                      analyze.
                    </>
                  ) : (
                    "Upload a PDF or Word resume."
                  )}
                </div>
                <input
                  ref={resumeInputRef}
                  type="file"
                  accept="application/pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleResumeUpload}
                />
                <button
                  type="button"
                  className="jmsp-primary-btn"
                  style={{ maxWidth: 220, marginTop: 0 }}
                  onClick={triggerResumePicker}
                  disabled={uploadingResume}
                >
                  {uploadingResume ? (
                    <>
                      <CircularProgress size={18} style={{ color: "#fff" }} />
                      Uploading…
                    </>
                  ) : resumeUrl ? (
                    "Replace resume"
                  ) : (
                    "Upload resume"
                  )}
                </button>
              </div>
            </div>

            <div className="jmsp-card">
              <div className="jmsp-step-label">Step 2 — Job</div>
              <div className="jmsp-tabs">
                <button
                  type="button"
                  className={`jmsp-tab ${activeTab === "description" ? "jmsp-tab-active" : ""}`}
                  onClick={() => setActiveTab("description")}
                >
                  Paste description
                </button>
                <button
                  type="button"
                  className={`jmsp-tab ${activeTab === "url" ? "jmsp-tab-active" : ""}`}
                  onClick={() => setActiveTab("url")}
                >
                  Job URL
                </button>
              </div>
              <label className="jmsp-input-label">
                {activeTab === "url"
                  ? "Job posting link"
                  : "Job description"}
              </label>
              {activeTab === "url" ? (
                <input
                  type="url"
                  className="jmsp-url-input"
                  placeholder="https://…"
                  value={jobUrl}
                  onChange={(e) => setJobUrl(e.target.value)}
                  disabled={checking}
                />
              ) : (
                <textarea
                  className="jmsp-textarea"
                  placeholder="Paste the full job description here…"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  disabled={checking}
                />
              )}
              {error && <div className="jmsp-error">{error}</div>}
              <button
                type="button"
                className="jmsp-primary-btn"
                onClick={handleCheckMatchScore}
                disabled={checking}
              >
                {checking ? (
                  <>
                    <CircularProgress size={18} style={{ color: "#fff" }} />
                    Checking…
                  </>
                ) : (
                  <>
                    <TrackChangesIcon style={{ fontSize: 20 }} />
                    Check Match Score
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {preview && (
          <div className="jmsp-card">
            <div className="jmsp-preview-block">
              <div className="jmsp-score-circle">
                <svg className="jmsp-circle-svg" viewBox="0 0 120 120">
                  <circle className="jmsp-circle-bg" cx="60" cy="60" r="54" />
                  <circle
                    className="jmsp-circle-progress"
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(score / 100) * 339.292} 339.292`,
                    }}
                  />
                </svg>
                <div className="jmsp-score-center">
                  <span className="jmsp-score-num">{score}%</span>
                  <span className="jmsp-score-sub">Match score</span>
                </div>
              </div>
            </div>

            {(jobInfo.job_title || jobInfo.hiring_corporation) && (
              <div className="jmsp-job-teaser">
                {jobInfo.job_title && (
                  <div>
                    <strong>Role:</strong> {jobInfo.job_title}
                  </div>
                )}
                {jobInfo.hiring_corporation && (
                  <div>
                    <strong>Company:</strong> {jobInfo.hiring_corporation}
                  </div>
                )}
              </div>
            )}

            <h2 className="jmsp-wall-title">Unlock your full assessment</h2>
            <p className="jmsp-wall-copy">
              Sign in to see matched skills, gaps, ATS-style insights, and
              personalized recommendations — the same tools thousands of
              seekers use on workBrook.
            </p>
            <div className="jmsp-auth-row">
              <Link
                to="/signin"
                className="jmsp-auth-btn jmsp-auth-primary"
                onClick={persistHandoffAndAuthPath}
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="jmsp-auth-btn jmsp-auth-secondary"
                onClick={persistHandoffAndAuthPath}
              >
                Create account
              </Link>
            </div>
            <button
              type="button"
              className="jmsp-primary-btn"
              style={{
                marginTop: 16,
                background: "#f1f5f9",
                color: "#334155",
              }}
              onClick={() => {
                setPreview(null);
                setError(null);
              }}
            >
              Try another job
            </button>
          </div>
        )}
      </main>

      <footer className="jmsp-footer">
        <a href="https://workbrook.com">WorkBrook</a>
        <Link to="/signin">Sign in</Link>
        <Link to="/signup">Sign up</Link>
      </footer>
    </div>
  );
};

export default JobMatchScorePreview;
